"use client"

import { useEffect, type ReactNode } from "react"

import { Spinner } from "@/app/_components/atoms/Spinner"
import { useCurrentUser } from "@/app/_hooks/useCurrentUser"

const RELOAD_KEY = "kanril-auth-fallback-reload"

/**
 * サーバーサイドで userId が取得できなかった場合のフォールバックコンポーネント。
 *
 * スーパーリロード時に Service Worker がバイパスされると、
 * サーバーに認証トークンが送信されず userId == null になる。
 * このコンポーネントはクライアント側で認証状態を確認し、
 * 認証済みであれば Service Worker の準備完了後に通常リロードを行う。
 *
 * children を渡すと、リロード処理はそのままに表示だけを差し替えられる。
 */
export default function AuthFallback({
  children,
}: {
  children?: ReactNode
} = {}) {
  const { stateReady, userId } = useCurrentUser()

  useEffect(() => {
    if (!stateReady) return

    // クライアント側で認証済みの場合、Service Worker 経由で再読み込み
    if (userId) {
      // 無限リロード防止: 5秒以内の再試行はスキップ
      const lastReload = sessionStorage.getItem(RELOAD_KEY)
      const now = Date.now()

      if (lastReload && now - parseInt(lastReload) < 5000) {
        return
      }

      if ("serviceWorker" in navigator) {
        void navigator.serviceWorker.ready.then(() => {
          sessionStorage.setItem(RELOAD_KEY, now.toString())
          window.location.reload()
        })
      }
    }
    // userId が null の場合は RedirectProvider が /auth へリダイレクトする
  }, [stateReady, userId])

  if (children != null) {
    return <>{children}</>
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center">
      <Spinner size="hg" />
      <p className="mt-12 text-center text-sm text-muted-foreground">
        画面が切り替わらない場合は
        <br />
        ページを再読み込みしてください。
      </p>
    </div>
  )
}
